import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, GripVertical, Save, ClipboardList } from 'lucide-react';
import { canEdit as canEditSection } from '@/lib/auth';
import { ADMIN_FEEDBACK_FORM } from '@/constants/testIds';

const FIELD_TYPES = [
    { value: 'text', label: 'Short text' },
    { value: 'textarea', label: 'Long text' },
    { value: 'rating', label: 'Rating (1–5)' },
    { value: 'yesno', label: 'Yes / No' },
    { value: 'multichoice', label: 'Multiple choice' },
    { value: 'number', label: 'Number' },
];

const genId = () => Math.random().toString(36).slice(2, 10);

export default function AdminFeedbackFormPage() {
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const canEdit = canEditSection('feedback_form');

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/feedback-form');
            setForm(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/admin/feedback-form', {
                title: form.title,
                description: form.description,
                fields: form.fields,
            });
            toast.success('Form saved.');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const updateField = (id, changes) => {
        setForm((f) => ({ ...f, fields: f.fields.map((x) => (x.id === id ? { ...x, ...changes } : x)) }));
    };
    const removeField = (id) => setForm((f) => ({ ...f, fields: f.fields.filter((x) => x.id !== id) }));
    const addField = () => {
        setForm((f) => ({
            ...f,
            fields: [
                ...f.fields,
                { id: genId(), label: 'New question', type: 'text', required: false, options: [] },
            ],
        }));
    };
    const moveField = (idx, dir) => {
        setForm((f) => {
            const next = [...f.fields];
            const target = idx + dir;
            if (target < 0 || target >= next.length) return f;
            const [item] = next.splice(idx, 1);
            next.splice(target, 0, item);
            return { ...f, fields: next };
        });
    };

    if (loading || !form) {
        return (
            <AdminLayout title="Feedback form" subtitle="Custom fields for mentors to fill after each session">
                <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/40 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout
            title="Feedback form"
            subtitle="Custom fields for mentors to fill after each session"
            actions={canEdit && (
                <Button onClick={save} disabled={saving} data-testid={ADMIN_FEEDBACK_FORM.save}>
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save form
                </Button>
            )}
        >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
                <div className="space-y-4">
                    <Card>
                        <CardContent className="space-y-3 p-6">
                            <div>
                                <Label>Form title</Label>
                                <Input value={form.title} disabled={!canEdit} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                            </div>
                            <div>
                                <Label>Form description</Label>
                                <Textarea rows={2} value={form.description || ''} disabled={!canEdit} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                            </div>
                        </CardContent>
                    </Card>

                    <div className="flex items-center justify-between">
                        <h3 className="font-display text-sm font-semibold">Fields ({form.fields.length})</h3>
                        {canEdit && (
                            <Button size="sm" variant="secondary" onClick={addField} data-testid={ADMIN_FEEDBACK_FORM.add_field}>
                                <Plus className="mr-1 h-4 w-4" /> Add field
                            </Button>
                        )}
                    </div>

                    {form.fields.map((f, idx) => (
                        <Card key={f.id}>
                            <CardContent className="space-y-3 p-5">
                                <div className="flex items-start gap-2">
                                    <div className="flex flex-col items-center gap-1 pt-1">
                                        <button type="button" onClick={() => moveField(idx, -1)} className="text-muted-foreground hover:text-foreground" aria-label="Move up">↑</button>
                                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                                        <button type="button" onClick={() => moveField(idx, 1)} className="text-muted-foreground hover:text-foreground" aria-label="Move down">↓</button>
                                    </div>
                                    <div className="flex-1 space-y-3">
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_auto]">
                                            <div>
                                                <Label className="text-xs">Question</Label>
                                                <Input value={f.label} disabled={!canEdit} onChange={(e) => updateField(f.id, { label: e.target.value })} />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Type</Label>
                                                <Select value={f.type} disabled={!canEdit} onValueChange={(v) => updateField(f.id, { type: v })}>
                                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        {FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="flex items-end gap-2">
                                                <div className="flex items-center gap-2">
                                                    <Switch checked={f.required} disabled={!canEdit} onCheckedChange={(v) => updateField(f.id, { required: v })} />
                                                    <span className="text-xs text-muted-foreground">Required</span>
                                                </div>
                                            </div>
                                        </div>
                                        {f.type === 'multichoice' && (
                                            <div>
                                                <Label className="text-xs">Options (comma separated)</Label>
                                                <Input
                                                    value={(f.options || []).join(', ')}
                                                    disabled={!canEdit}
                                                    onChange={(e) => updateField(f.id, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                                                    placeholder="e.g. Excellent, Good, Average, Poor"
                                                />
                                            </div>
                                        )}
                                        <Input
                                            className="text-xs"
                                            value={f.help_text || ''}
                                            disabled={!canEdit}
                                            onChange={(e) => updateField(f.id, { help_text: e.target.value })}
                                            placeholder="Optional help text"
                                        />
                                    </div>
                                    {canEdit && (
                                        <Button size="icon" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => removeField(f.id)}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                <aside className="lg:sticky lg:top-24 lg:self-start">
                    <Card>
                        <CardContent className="p-5">
                            <div className="mb-3 flex items-center gap-2 text-sm">
                                <ClipboardList className="h-4 w-4 text-primary" />
                                <h3 className="font-display font-semibold">Live preview</h3>
                            </div>
                            <div className="space-y-3 rounded-lg border border-border/60 bg-background p-4">
                                <div className="font-display text-lg font-semibold">{form.title || 'Untitled form'}</div>
                                {form.description && <div className="text-xs text-muted-foreground">{form.description}</div>}
                                {form.fields.map((f) => (
                                    <div key={f.id} className="text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium">{f.label || '—'}</span>
                                            {f.required && <Badge variant="outline" className="px-1.5 py-0 text-[10px]">required</Badge>}
                                        </div>
                                        <div className="mt-1 text-xs text-muted-foreground">Type: {f.type}</div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </aside>
            </div>
        </AdminLayout>
    );
}
